/**
 * URL state for the Elections Tool (Website ADRs 0003 and 0005).
 *
 * `person` and `election` are identity; `person` wins. Everything else is
 * Tool state that changes what is shown, never which record the URL names.
 * Parameters are written in one fixed order so equal states give equal URLs.
 */

export type CycleStatus = "current" | "upcoming" | "past";
export const CYCLE_STATUSES: readonly CycleStatus[] = ["current", "upcoming", "past"];

export interface DirectoryFilters {
  q: string;
  year: string | null;
  status: CycleStatus | null;
  /** A Body jurisdiction as Backbone reports it: city, county, state… */
  jurisdiction: string | null;
}

export type View =
  | { kind: "directory"; filters: DirectoryFilters }
  | { kind: "election"; cycle: string; ballot: string | null; race: string | null }
  | { kind: "person"; person: string; from: string | null; race: string | null };

export type ResponseFilter = "completed" | "no_response" | "could_not_reach" | "refused";
export const RESPONSE_FILTERS: readonly ResponseFilter[] = ["completed", "no_response", "could_not_reach", "refused"];

/** Election view filters: Tool state written after identity and ballot/race. */
export interface ElectionFilters {
  endorsed: boolean;
  incumbents: boolean;
  /** Null means every status; an explicit list narrows it. */
  responses: ResponseFilter[] | null;
}

export const NO_ELECTION_FILTERS: ElectionFilters = { endorsed: false, incumbents: false, responses: null };

function param(params: URLSearchParams, name: string): string | null {
  const value = params.get(name)?.trim();
  return value ? value : null;
}

export function parseUrl(url: URL): View {
  const p = url.searchParams;
  const person = param(p, "person");
  if (person) return { kind: "person", person, from: param(p, "from"), race: param(p, "race") };
  const cycle = param(p, "election");
  if (cycle) return { kind: "election", cycle, ballot: param(p, "ballot"), race: param(p, "race") };
  const year = param(p, "year");
  const status = param(p, "status");
  return {
    kind: "directory",
    filters: {
      q: p.get("q") ?? "",
      year: year && /^\d{4}$/.test(year) ? year : null,
      status: CYCLE_STATUSES.includes(status as CycleStatus) ? (status as CycleStatus) : null,
      jurisdiction: param(p, "jurisdiction"),
    },
  };
}

export function parseElectionFilters(url: URL): ElectionFilters {
  const p = url.searchParams;
  const raw = param(p, "responses");
  let responses: ResponseFilter[] | null = null;
  if (raw === "none") responses = [];
  else if (raw) {
    const requested = raw.split(",");
    if (
      requested.length > 0 &&
      requested.every((value) =>
        RESPONSE_FILTERS.includes(value as ResponseFilter)
      )
    ) {
      responses = RESPONSE_FILTERS.filter((value) =>
        requested.includes(value)
      );
    }
  }
  return {
    endorsed: p.get("endorsed") === "1",
    incumbents: p.get("incumbents") === "1",
    responses,
  };
}

/**
 * The URL for a view on the page at `base`. Drops every other parameter the
 * page carried, so a URL names exactly one state.
 */
export function buildUrl(view: View, base: URL, electionFilters?: ElectionFilters): string {
  const out = new URL(base.href);
  out.search = "";
  out.hash = "";
  const pairs: [string, string | null][] = [];
  if (view.kind === "person") {
    pairs.push(["person", view.person], ["from", view.from], ["race", view.race]);
  } else if (view.kind === "election") {
    pairs.push(["election", view.cycle], ["ballot", view.ballot], ["race", view.race]);
    if (electionFilters) {
      pairs.push(
        ["endorsed", electionFilters.endorsed ? "1" : null],
        ["incumbents", electionFilters.incumbents ? "1" : null],
        [
          "responses",
          electionFilters.responses === null
            ? null
            : electionFilters.responses.length === 0
              ? "none"
              : RESPONSE_FILTERS.filter((value) =>
                  electionFilters.responses!.includes(value)
                ).join(","),
        ]
      );
    }
  } else {
    const f = view.filters;
    pairs.push(["q", f.q.trim() || null], ["year", f.year], ["status", f.status], ["jurisdiction", f.jurisdiction]);
  }
  // Commas are safe in a query value; keeping them literal keeps URLs legible.
  const search = pairs
    .filter((pair): pair is [string, string] => pair[1] !== null && pair[1] !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(v).replace(/%2C/g, ",")}`)
    .join("&");
  return search ? `${out.origin}${out.pathname}?${search}` : `${out.origin}${out.pathname}`;
}

/** True when two views name the same record, whatever their state. */
export function sameIdentity(a: View, b: View): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === "election" && b.kind === "election") return a.cycle === b.cycle;
  if (a.kind === "person" && b.kind === "person") return a.person === b.person;
  return true;
}
